import { useCallback, useEffect, useState } from 'react';
import {
	fetchOpenQuestionsForStudent,
	resolveSongQuestion,
} from '../../../../services/SongQuestionsService';
import { openQuestions } from '../../../../services/songQuestionsModel';

/**
 * "From home" data, owned by the PAGE (pass 3 C2, 2026-09-28). The questions
 * render in the Arrive group, which is mounted twice (the rail, and the notes
 * column when the rail is hidden; only one is painted). When the component
 * fetched for itself, two mounts meant two reads and two copies of the list,
 * so an "Answered" tapped in one could come back in the other after a resize.
 * One hook, one list, handed to both mounts: the useCarryForward shape.
 *
 * Returns { questions, error, resolving, markAnswered }. `error` is a load
 * failure, which must never read as "the family asked nothing".
 */
const useFamilyQuestions = ({ studentId, currentTeacherId = null }) => {
	const [questions, setQuestions] = useState([]);
	const [resolving, setResolving] = useState(() => new Set());
	const [error, setError] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (!studentId) {
			setQuestions([]);
			return undefined;
		}
		(async () => {
			try {
				const rows = await fetchOpenQuestionsForStudent(studentId);
				if (!cancelled) {
					setQuestions(openQuestions(rows));
					setError(false);
				}
			} catch (e) {
				// A load failure must not read as "the family asked nothing" — that
				// is a different and worse claim, and it is the one that would make
				// a teacher walk past a real question.
				console.error('Failed to load family questions', e);
				if (!cancelled) setError(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [studentId]);

	const markAnswered = useCallback(
		async (question) => {
			setResolving((prev) => new Set(prev).add(question.id));
			try {
				await resolveSongQuestion(question.id, { byUid: currentTeacherId });
				setQuestions((prev) => prev.filter((q) => q.id !== question.id));
			} catch (e) {
				console.error('Failed to resolve family question', e);
				setResolving((prev) => {
					const next = new Set(prev);
					next.delete(question.id);
					return next;
				});
			}
		},
		[currentTeacherId]
	);

	return { questions, error, resolving, markAnswered };
};

export default useFamilyQuestions;
